'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterBarProps {
  onFilterChange: (filters: FilterState) => void;
}

export interface FilterState {
  search: string;
  genre: string;
  rating: string;
  sortBy: string;
}

const genres = [
  'All Genres',
  'Fiction',
  'Science Fiction',
  'Fantasy',
  'Mystery',
  'Thriller',
  'Historical Fiction',
  'Romance',
  'Non-Fiction',
  'Biography',
  'Self-Help',
];

const ratings = [
  'All Ratings',
  '5 Stars',
  '4+ Stars',
  '3+ Stars',
];

const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'highest', label: 'Highest Rated' },
  { value: 'lowest', label: 'Lowest Rated' },
];

export default function FilterBar({ onFilterChange }: FilterBarProps) {
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    genre: 'All Genres',
    rating: 'All Ratings',
    sortBy: 'newest',
  });

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  return (
    <div className="sticky top-20 z-40 bg-background/95 backdrop-blur-xl border-b border-border py-6">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Icon
                name="MagnifyingGlassIcon"
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by title, author, or keyword..."
                className="w-full pl-12 pr-4 py-3 rounded-lg bg-card border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* Genre */}
          <div>
            <select
              value={filters.genre}
              onChange={(e) => handleFilterChange('genre', e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-card border border-border text-foreground focus:outline-none focus:border-primary transition-colors"
            >
              {genres.map((genre) => (
                <option key={`filter_genre_${genre}`} value={genre}>
                  {genre}
                </option>
              ))}
            </select>
          </div>

          {/* Rating */}
          <div>
            <select
              value={filters.rating}
              onChange={(e) => handleFilterChange('rating', e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-card border border-border text-foreground focus:outline-none focus:border-primary transition-colors"
            >
              {ratings.map((rating) => (
                <option key={`filter_rating_${rating}`} value={rating}>
                  {rating}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Sort & Results Count */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mt-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Sort by:</span>
            <div className="flex gap-2">
              {sortOptions.map((option) => (
                <button
                  key={`sort_${option.value}`}
                  onClick={() => handleFilterChange('sortBy', option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filters.sortBy === option.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-foreground hover:bg-card/80'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <span className="text-sm text-muted-foreground">
            Showing 247 reviews
          </span>
        </div>
      </div>
    </div>
  );
}